/**
 * Presentation Links Interface
 * Displays button links that can open URLs or trigger actions
 * This is a presentation-only field (no data stored with the item)
 */

import { Button } from '@/components/ui/button';
import { ExternalLink, Play } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Link {
    label: string;
    icon?: string;
    type?: string;  // Allow any string, will validate at runtime
    actionType?: string;
    url?: string;
    flow?: string;
}

interface PresentationLinksProps {
    links?: Link[];
    disabled?: boolean;
    primaryKey?: string | number;
    itemData?: Record<string, unknown>;
    className?: string;
}

export function PresentationLinks({
    links = [],
    disabled = false,
    primaryKey,
    itemData = {},
    className,
}: PresentationLinksProps) {
    if (!links || links.length === 0) {
        return (
            <div className={cn('text-sm text-muted-foreground italic', className)}>
                No links configured
            </div>
        );
    }

    // Interpolate URL with item data using simple template syntax {{field}}
    const interpolateUrl = (url: string): string => {
        if (!url) return '';
        return url.replace(/\{\{(\w+)\}\}/g, (_, key) => {
            if (key === 'id' && primaryKey) return String(primaryKey);
            return String(itemData[key] ?? '');
        });
    };

    const getButtonVariant = (type?: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
        switch (type) {
            case 'primary':
                return 'default';
            case 'danger':
                return 'destructive';
            case 'info':
            case 'success':
            case 'warning':
            case 'normal':
            default:
                return 'secondary';
        }
    };

    const getButtonClass = (type?: string): string => {
        switch (type) {
            case 'info':
                return 'bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 border-blue-500/20';
            case 'success':
                return 'bg-green-500/10 text-green-600 hover:bg-green-500/20 border-green-500/20';
            case 'warning':
                return 'bg-yellow-500/10 text-yellow-600 hover:bg-yellow-500/20 border-yellow-500/20';
            default:
                return '';
        }
    };

    const handleClick = (link: Link) => {
        if (link.actionType === 'flow') {
            // Flow execution not implemented yet
            console.log('Flow execution not implemented:', link.flow);
            return;
        }

        if (link.url) {
            const interpolatedUrl = interpolateUrl(link.url);
            if (interpolatedUrl.startsWith('/')) {
                // Internal link - for now just use window.location
                window.location.href = interpolatedUrl;
            } else {
                // External link
                window.open(interpolatedUrl, '_blank', 'noopener,noreferrer');
            }
        }
    };

    return (
        <div className={cn('flex flex-wrap gap-2', className)}>
            {links.map((link, index) => {
                const isNewItem = !primaryKey || primaryKey === '+';
                const isDisabled = disabled || (link.actionType === 'flow' && isNewItem);

                return (
                    <Button
                        key={index}
                        variant={getButtonVariant(link.type)}
                        className={cn(getButtonClass(link.type))}
                        disabled={isDisabled}
                        onClick={() => handleClick(link)}
                    >
                        {link.actionType === 'flow' ? (
                            <Play className="mr-2 h-4 w-4" />
                        ) : link.url && !link.url.startsWith('/') ? (
                            <ExternalLink className="mr-2 h-4 w-4" />
                        ) : null}
                        {link.label || 'Button'}
                    </Button>
                );
            })}
        </div>
    );
}
